import { Component, Input } from '@angular/core';

/**
 * Member portal — shared, presentational membership card
 * (ported 2026-08-07 from
 * atenza-front/src/app/portals/member/pages/membership-card/).
 *
 * This is the SAME pure-CSS flip card the funder portal issues from
 * (portals/funder/dependents/view-membership-card), extracted into a
 * presentational component so the member Digital Card page — and, later, the
 * member dashboard rail — render one identical card instead of three drifting
 * copies.
 *
 * NO DATA FETCHING HERE. The parent passes the principal dependent and the
 * already-resolved scheme name. That is deliberate: it keeps this component
 * free of services, which is why it can safely live in SharedModule and be
 * reused from any module (see the wiring note at the bottom of this comment).
 *
 * WeMAS DIVERGENCE — SCHEME PALETTES (do not "fix" this back to Atenza's list)
 * ---------------------------------------------------------------------------
 * Atenza's cardSchemeKey() maps to pearl / silk / wax / alkane / lipid —
 * those are SALUTEM's Zimbabwe product names and mean nothing in Malawi.
 * WeMAS already owns its own palette set in the funder card
 * (view-membership-card.component.ts:99-107 + .scss:329-385):
 *
 *     medhealth | silver | gold | platinum | default
 *
 * This component uses the WeMAS set, verbatim, so a member sees exactly the
 * same card the funder issued them. It also inherits WeMAS's safer fallback:
 * an unmatched scheme name returns 'default' (MEDHEALTH teal) rather than
 * null, so no member is ever shown a "card design is being set up" apology for
 * a card that has in fact been issued. Atenza's #noPalette branch is therefore
 * not ported — it is unreachable by construction here.
 *
 * BRANDING: the logo is assets/images/logo-long.png (MEDHEALTH) and the back
 * footer reads MEDHEALTH — matching the funder card. Atenza's
 * assets/images/salutem.png is NOT used, even though the file survives in the
 * WeMAS assets folder as clone residue.
 *
 * NO SUBSCRIPTIONS: this component holds no Subscription, no timer and no
 * listener, so it correctly implements no OnDestroy. The CSS `auto-rotate`
 * spin is a pure CSS animation (@keyframes mc-auto-spin) — nothing to tear
 * down, and it honours prefers-reduced-motion.
 *
 * MODULE WIRING (owned by the orchestrator, not by this folder): declare +
 * export this component in src/app/shared/shared.module.ts, mirroring Atenza
 * (atenza-front shared.module.ts:2, 102, 203). PagesModule already imports
 * SharedModule, so the Digital Card page picks it up with no further change.
 */
@Component({
  selector: 'app-member-membership-card',
  templateUrl: './membership-card.component.html',
  styleUrls: ['./membership-card.component.scss'],
})
export class MemberMembershipCardComponent {
  /**
   * Principal dependent carrying the membership_card_* fields.
   * Typed `any` (as upstream) so a plain API object works as well as a
   * Dependent model instance — every field read below is verified present on
   * core/models/dependent.ts:50-56, 7-14.
   */
  @Input() dependent: any = null;

  /** Resolved scheme name — drives the palette and the on-card watermark. */
  @Input() schemeName = '';

  /** Smaller footprint, for a dashboard side rail. */
  @Input() compact = false;

  /** Spin on its own (showcase mode). Clicking is disabled while spinning. */
  @Input() autoRotate = false;

  public flipped = false;

  /** Click flips the card — unless it is the auto-rotating showcase. */
  onCardClick(): void {
    if (!this.autoRotate) {
      this.flipCard();
    }
  }

  /** Has a card actually been issued and captured? */
  hasCard(): boolean {
    return !!this.dependent?.membership_card_number;
  }

  /** Space the number every 4 characters — standard card visual style. */
  formatCardNumber(num?: string): string {
    if (!num) {
      return '';
    }
    return num.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
  }

  /** Member number + beneficiary suffix, e.g. `0000190-01`. */
  memberSuffixLabel(): string {
    if (!this.dependent) {
      return '';
    }
    return `${this.dependent.member_number}-${this.dependent.beneficiary_number}`;
  }

  /**
   * Palette key for the `[data-scheme]` SCSS hook. Substring match on the
   * scheme name, case-insensitive — identical logic to the funder card
   * (view-membership-card.component.ts:99-107) so both portals resolve the
   * same scheme to the same palette. Unmatched names fall back to the
   * MEDHEALTH teal 'default'.
   */
  cardSchemeKey(): 'medhealth' | 'silver' | 'gold' | 'platinum' | 'default' {
    const name = (this.schemeName || '').toString().trim().toLowerCase();
    if (!name) {
      return 'default';
    }
    if (name.includes('platinum')) {
      return 'platinum';
    }
    if (name.includes('gold')) {
      return 'gold';
    }
    if (name.includes('silver')) {
      return 'silver';
    }
    if (name.includes('medhealth')) {
      return 'medhealth';
    }
    return 'default';
  }

  /** Toggle the front/back face. */
  flipCard(): void {
    this.flipped = !this.flipped;
  }
}
